import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

/**
 * SimModeBanner
 *
 * Renders a prominent, non-dismissable banner pinned to the top of the
 * viewport when the application is running in migration-simulation (sim)
 * mode. In sim mode, actions are confined to a sandbox Account and do
 * NOT reach any external system of record (no QuickBooks sync, no payroll
 * export, no downstream side effects). This banner exists so the operator
 * running a migration-confidence dry run can always see, at a glance, that
 * what they are looking at is a simulation — not the live customer data.
 *
 * The flag is plumbed from the masquerade / account-user refresh response
 * as `accountUserStore.sim_mode === true`. See docs/testids.md for the
 * deterministic testid used to assert this banner in tests and in any
 * browser automation that verifies migration runs.
 */
const bannerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10000,
    backgroundColor: '#ffb400',
    color: '#1a1a1a',
    padding: '10px 16px',
    textAlign: 'center',
    fontWeight: 700,
    fontSize: '14px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    borderBottom: '3px solid #b36b00',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
    fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
};

const SimModeBanner = ({ simMode }) => {
    if (!simMode) {
        return null;
    }

    return (
        <div role="status" aria-live="polite" data-testid="sim-mode-banner" style={bannerStyle}>
            SIM MODE &mdash; no external sync
        </div>
    );
};

SimModeBanner.propTypes = {
    simMode: PropTypes.bool,
};

SimModeBanner.defaultProps = {
    simMode: false,
};

function mapStateToProps(state) {
    // The sim-mode flag is surfaced by the backend masquerade / account-user
    // response (see Arcaio/minute7-backend#815). When not present the
    // selector resolves to `false` so the banner stays hidden under normal
    // (non-simulation) operation.
    const accountUserStore = state.accountUserStore || {};
    return {
        simMode: accountUserStore.sim_mode === true,
    };
}

export { SimModeBanner };
export default connect(mapStateToProps)(SimModeBanner);
